(() => {
  if (window.TAImagePreview) return;

  window.TAImagePreview = true;

  const triggerSelector = '[data-ta-image-preview]';
  const zoomTriggerSelector = `${triggerSelector}[data-ta-image-preview-zoom]`;
  const zoomLevels = [1, 1.5, 2, 3];
  let modal = null;
  let modalWindow = null;
  let modalClose = null;
  let modalImage = null;
  let zoomViewport = null;
  let zoomStage = null;
  let zoomControls = null;
  let zoomReadout = null;
  let zoomInButton = null;
  let zoomOutButton = null;
  let zoomLevelIndex = 0;
  let savedScrollPosition = null;
  let activeTrigger = null;
  let stylesheetPromise = null;
  let openRequestId = 0;

  const ensureZoomStylesheet = (trigger) => {
    const href = trigger.getAttribute('data-ta-image-preview-zoom-stylesheet');
    if (!href) return Promise.resolve();
    if (stylesheetPromise) return stylesheetPromise;

    let stylesheet = document.querySelector('link[data-ta-image-preview-zoom-stylesheet]');
    if (!stylesheet) {
      stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = href;
      stylesheet.setAttribute('data-ta-image-preview-zoom-stylesheet', '');
      stylesheetPromise = new Promise((resolve) => {
        stylesheet.addEventListener('load', resolve, { once: true });
        stylesheet.addEventListener('error', resolve, { once: true });
        document.head.appendChild(stylesheet);
      });
      return stylesheetPromise;
    }

    if (stylesheet.sheet) {
      stylesheetPromise = Promise.resolve();
      return stylesheetPromise;
    }

    stylesheetPromise = new Promise((resolve) => {
      stylesheet.addEventListener('load', resolve, { once: true });
      stylesheet.addEventListener('error', resolve, { once: true });
    });
    return stylesheetPromise;
  };

  const restoreScrollPosition = () => {
    if (!savedScrollPosition) return;
    window.scrollTo(savedScrollPosition.x, savedScrollPosition.y);
  };

  const updateZoomControls = () => {
    if (!zoomReadout) return;

    const zoom = zoomLevels[zoomLevelIndex];
    const percent = Math.round(zoom * 100);
    zoomReadout.textContent = `${percent}%`;
    zoomReadout.setAttribute('aria-label', `Reset zoom to 100% (current zoom: ${percent}%)`);
    zoomOutButton.disabled = zoomLevelIndex === 0;
    zoomInButton.disabled = zoomLevelIndex === zoomLevels.length - 1;
  };

  const updateZoom = (preservePosition = false) => {
    if (!zoomViewport || !zoomStage || !modalImage?.naturalWidth || !modalImage.naturalHeight) return;

    const viewportWidth = zoomViewport.clientWidth;
    const viewportHeight = zoomViewport.clientHeight;
    if (!viewportWidth || !viewportHeight) return;

    const oldWidth = zoomStage.clientWidth || zoomViewport.scrollWidth;
    const oldHeight = zoomStage.clientHeight || zoomViewport.scrollHeight;
    const oldScrollX = zoomViewport.scrollLeft;
    const oldScrollY = zoomViewport.scrollTop;
    const oldCenterX = oldWidth > viewportWidth ? (oldScrollX + viewportWidth / 2) / oldWidth : 0.5;
    const oldCenterY = oldHeight > viewportHeight ? (oldScrollY + viewportHeight / 2) / oldHeight : 0.5;
    const fitScale = Math.min(
      1,
      viewportWidth / modalImage.naturalWidth,
      viewportHeight / modalImage.naturalHeight
    );
    const zoom = zoomLevels[zoomLevelIndex];
    const imageWidth = Math.round(modalImage.naturalWidth * fitScale * zoom);
    const imageHeight = Math.round(modalImage.naturalHeight * fitScale * zoom);
    const stageWidth = Math.max(viewportWidth, imageWidth);
    const stageHeight = Math.max(viewportHeight, imageHeight);

    zoomStage.style.width = `${stageWidth}px`;
    zoomStage.style.height = `${stageHeight}px`;
    modalImage.style.width = `${imageWidth}px`;
    updateZoomControls();

    if (preservePosition) {
      zoomViewport.scrollLeft = Math.max(0, oldCenterX * stageWidth - viewportWidth / 2);
      zoomViewport.scrollTop = Math.max(0, oldCenterY * stageHeight - viewportHeight / 2);
    } else {
      zoomViewport.scrollLeft = 0;
      zoomViewport.scrollTop = 0;
    }
  };

  const clearZoomMode = () => {
    if (!modal) return;

    modal.classList.remove('ta-image-preview-modal--zoom');
    modal.removeAttribute('aria-label');
    modalImage.style.removeProperty('width');

    if (zoomViewport) {
      modalWindow.insertBefore(modalImage, modalClose.nextSibling);
      zoomViewport.remove();
      zoomViewport = null;
      zoomStage = null;
    }

    if (zoomControls) {
      zoomControls.remove();
      zoomControls = null;
      zoomReadout = null;
      zoomInButton = null;
      zoomOutButton = null;
    }
  };

  const enableZoomMode = (label) => {
    if (!zoomViewport) {
      zoomControls = document.createElement('div');
      zoomControls.className = 'ta-image-preview-modal__zoom-controls';
      zoomControls.setAttribute('role', 'toolbar');
      zoomControls.setAttribute('aria-label', 'Image zoom controls');
      zoomControls.innerHTML = `
        <button type="button" class="ta-image-preview-modal__zoom-button" data-ta-image-preview-zoom-out aria-label="Zoom out" title="Zoom out">−</button>
        <button type="button" class="ta-image-preview-modal__zoom-button ta-image-preview-modal__zoom-reset" data-ta-image-preview-zoom-reset aria-label="Reset zoom to 100%" aria-live="polite" aria-atomic="true">100%</button>
        <button type="button" class="ta-image-preview-modal__zoom-button" data-ta-image-preview-zoom-in aria-label="Zoom in" title="Zoom in">+</button>
      `;
      zoomReadout = zoomControls.querySelector('[data-ta-image-preview-zoom-reset]');
      zoomOutButton = zoomControls.querySelector('[data-ta-image-preview-zoom-out]');
      zoomInButton = zoomControls.querySelector('[data-ta-image-preview-zoom-in]');
      zoomControls.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;

        if (button === zoomInButton && zoomLevelIndex < zoomLevels.length - 1) {
          zoomLevelIndex += 1;
          updateZoom(true);
        } else if (button === zoomOutButton && zoomLevelIndex > 0) {
          zoomLevelIndex -= 1;
          updateZoom(true);
        } else if (button === zoomReadout) {
          zoomLevelIndex = 0;
          updateZoom(false);
        }
      });

      zoomViewport = document.createElement('div');
      zoomViewport.className = 'ta-image-preview-modal__viewport';
      zoomViewport.tabIndex = 0;
      zoomViewport.setAttribute('role', 'region');
      zoomViewport.setAttribute('aria-label', 'Image details. Use arrow keys or scroll to view.');
      zoomStage = document.createElement('div');
      zoomStage.className = 'ta-image-preview-modal__stage';
      zoomViewport.appendChild(zoomStage);
      zoomStage.appendChild(modalImage);

      modalWindow.insertBefore(zoomControls, modalClose.nextSibling);
      modalWindow.appendChild(zoomViewport);
    }

    modal.classList.add('ta-image-preview-modal--zoom');
    modal.setAttribute('aria-label', label);
    zoomLevelIndex = 0;
    updateZoomControls();
  };

  const createModal = () => {
    modal = document.createElement('modal-dialog');
    modal.className = 'modal ta-image-preview-modal fixed top-0 left-0 w-full h-full flex items-center justify-center';
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('tabindex', '-1');
    modal.innerHTML = `
      <div class="modal__window ta-image-preview-modal__window">
        <button type="button" class="ta-image-preview-modal__close js-close-modal" aria-label="Close image preview">
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
          </svg>
        </button>
        <img class="ta-image-preview-modal__image" src="" alt="">
      </div>
    `;

    modalWindow = modal.querySelector('.ta-image-preview-modal__window');
    modalClose = modal.querySelector('.ta-image-preview-modal__close');
    modalImage = modal.querySelector('.ta-image-preview-modal__image');
    modalImage.addEventListener('load', () => updateZoom());
    modal.addEventListener('click', (event) => {
      const closeTarget = event.target === modal || event.target.closest('.js-close-modal');
      if (!closeTarget) return;

      if (modal.hasAttribute('open')) closePreview();
      else requestAnimationFrame(restoreScrollPosition);
    });
    document.body.appendChild(modal);
  };

  const openPreview = async (trigger) => {
    const requestId = ++openRequestId;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const src = isMobile
      ? trigger.getAttribute('data-ta-image-preview-mobile-src') || trigger.getAttribute('data-ta-image-preview-src')
      : trigger.getAttribute('data-ta-image-preview-src');
    if (!src) return;

    const zoomEnabled = trigger.hasAttribute('data-ta-image-preview-zoom');
    const scrollPosition = { x: window.scrollX, y: window.scrollY };
    if (zoomEnabled) await ensureZoomStylesheet(trigger);
    if (requestId !== openRequestId) return;

    if (!modal) createModal();

    if (zoomEnabled) {
      enableZoomMode(trigger.getAttribute('data-ta-image-preview-zoom-label') || 'Image preview');
    } else {
      clearZoomMode();
    }

    modalImage.src = src;
    modalImage.alt = isMobile
      ? trigger.getAttribute('data-ta-image-preview-mobile-alt') || trigger.getAttribute('data-ta-image-preview-alt') || ''
      : trigger.getAttribute('data-ta-image-preview-alt') || '';
    savedScrollPosition = scrollPosition;
    activeTrigger = trigger;

    if (typeof modal.open === 'function') {
      modal.open(trigger);
    } else {
      modal.openedBy = trigger;
      modal.setAttribute('open', '');
      modal.focus({ preventScroll: true });
    }

    if (zoomEnabled) {
      updateZoom();
      zoomViewport.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(triggerSelector);
    if (!trigger) return;

    event.preventDefault();
    void openPreview(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal?.hasAttribute('open')) {
      event.preventDefault();
      closePreview();
    }
  });

  window.addEventListener('resize', () => {
    if (modal?.hasAttribute('open') && zoomViewport) updateZoom(true);
  });

  function closePreview() {
    if (!modal) return;

    if (typeof modal.close === 'function') {
      modal.close();
      requestAnimationFrame(restoreScrollPosition);
    } else {
      modal.removeAttribute('open');
      activeTrigger?.focus({ preventScroll: true });
      restoreScrollPosition();
    }
  }

  const firstZoomTrigger = document.querySelector(zoomTriggerSelector);
  if (firstZoomTrigger) void ensureZoomStylesheet(firstZoomTrigger);
})();
